import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, RodapeDaLista, usePaginacao,
} from '../BarraDeLista';
import { EscolhaDeBalanca, useEquipamentos } from '../Ficha';
import { numero } from '../formato';
import { tecladoDaTabela } from '../tecladoDaTabela';
import { pesarSeca, verEstufa } from '../api/triviais';
import type { Estufa as DadosDaEstufa, RespostaDaPesagem } from '../api/triviais';
import { useSessao } from '../sessao';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import {
  Aviso, Carregando, CampoNumerico, ErroDaApi, EscolhaPorCodigo, RespostaDaAcao,
  Selo, TabelaDeLancamento, useEstreito,
} from '../ui';
import type { LinhaDeLancamento } from '../ui';
import { JanelaDeCorrecao, correcaoPossivel, tempoDeEstufa, useRelogio } from './Triviais';
import type { Correcao } from './Triviais';
import {
  SeloDaEstufa, horasDoAlerta, passouDoAlerta, useAlertaDaEstufa,
} from './alertaDaEstufa';

/**
 * Bancada › Estufa — a pesagem seca de todas as cápsulas (onda 290).
 *
 * «A massa da cápsula+solo seco é feita 24h depois» (Humberto,
 * 06/10/2026). De manhã o laboratorista tira da estufa as cápsulas de
 * vários ensaios, senta na balança e pesa na ordem em que elas saem. O que
 * ele tem na mão é o número da cápsula: digita «41», a tela acha o ensaio
 * (a cápsula na estufa é única — RN-237), ele digita a massa e aperta
 * Enter. O w aparece, e o aviso de tendência também, enquanto a cápsula
 * ainda está na bancada.
 *
 * A pesagem grava pela MESMA rota da aba «Pesagem seca» da ficha
 * (`/estufa/pesagens`): as duas portas existem, e o servidor recusa a
 * segunda pesagem da mesma cápsula dizendo quem pesou e quando (RN-235).
 *
 * Onda 305 (RN-250): a cápsula fica aqui sem prazo, mas a que passou das
 * horas do ajuste (24 h) entra em alerta — o indicador «Há mais de 24 h»
 * na faixa, o selo na linha, e o filtro «Só as de mais de 24 h», que o
 * link do Painel abre ligado (`?alerta=1`). Nada bloqueia a pesagem.
 *
 * Onda 310 (spec 06 §7.7): a Estufa em série no celular. «Pesar» vem antes
 * dos contadores; cada cartão de «Na estufa» tem o seu «Pesar», que escolhe
 * a cápsula, sobe até o formulário e põe o cursor na massa seca; depois de
 * gravar, o cursor volta à cápsula (antes ia para a balança); e a balança
 * fica lembrada no aparelho. A ordem do celular é do `celular.css` (310).
 */

const FUSO = 'America/Sao_Paulo';

/**
 * A balança da Estufa, lembrada no aparelho (spec 06 §7.7): quem pesa em
 * série de manhã pesa na mesma balança todo dia. O armazenamento pode faltar
 * (janela anônima, dados bloqueados) — aí a tela só não lembra.
 */
const BALANCA_LEMBRADA = 'labmanager.estufa.balanca';
const lerBalancaLembrada = () => {
  try { return localStorage.getItem(BALANCA_LEMBRADA) ?? ''; } catch { return ''; }
};
const lembrarBalanca = (id: string) => {
  try { localStorage.setItem(BALANCA_LEMBRADA, id); } catch { /* sem armazenamento */ }
};
const quando = (v: unknown) => (v ? new Date(String(v)).toLocaleString('pt-BR', {
  timeZone: FUSO, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
}).replace(',', '') : '—');
const hora = (ms: number) => new Date(ms).toLocaleTimeString('pt-BR',
  { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });

/** A rota da ficha de cada tipo de linha. */
const fichaDe = (l: any) => `/ensaio/${l.ensaio_id}/${l.tipo}`;

/**
 * O nome do ensaio de cada tipo de linha — desde a onda 294 (RN-241) a
 * Estufa recebe as cápsulas de todos os ensaios que medem umidade.
 */
const NOME_DO_TIPO: Record<string, string> = {
  umidade: 'Teor de umidade',
  'limite-liquidez': 'Limite de liquidez',
  'limite-plasticidade': 'Limite de plasticidade',
  compactacao: 'Compactação',
  adensamento: 'Adensamento',
  'cisalhamento-direto': 'Cisalhamento direto',
  triaxial: 'Triaxial',
  granulometria: 'Granulometria',
  'massa-especifica': 'Massa específica',
};

export function Estufa() {
  const { eu, pode } = useSessao();
  const equip = useEquipamentos();
  const [d, setD] = useState<DadosDaEstufa | null>(null);
  const [erroCarga, setErroCarga] = useState<unknown>(null);
  const [balancaId, setBalancaId] = useState('');
  const [linhaId, setLinhaId] = useState('');
  const [massa, setMassa] = useState('');
  const [busca, setBusca] = useState('');
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ultima, setUltima] = useState<RespostaDaPesagem | null>(null);
  const [corrigindo, setCorrigindo] = useState<{ linha: any; c: Correcao; tipo: any } | null>(null);
  const [lembrada] = useState(lerBalancaLembrada);
  const estreito = useEstreito();
  const secaoPesar = useRef<HTMLElement>(null);
  const caixaCapsula = useRef<HTMLDivElement>(null);
  const agora = useRelogio(d?.agora);
  const horas = useAlertaDaEstufa(d?.alertaHoras);
  const [sp, setSp] = useSearchParams();
  const soAlerta = sp.get('alerta') === '1';
  const mudarSoAlerta = (v: boolean) => {
    const n = new URLSearchParams(sp);
    if (v) n.set('alerta', '1'); else n.delete('alerta');
    setSp(n, { replace: true });
  };
  /* Onda 364 (spec 13 §6.5): «Minhas cápsulas» — as que eu lancei. É onde o
     aviso «Há cápsulas na estufa para você pesar» leva (`?minhas=1`). */
  const soMinhas = sp.get('minhas') === '1';
  const mudarSoMinhas = (v: boolean) => {
    const n = new URLSearchParams(sp);
    if (v) n.set('minhas', '1'); else n.delete('minhas');
    setSp(n, { replace: true });
  };
  const limparFiltros = () => {
    const n = new URLSearchParams(sp);
    n.delete('alerta'); n.delete('minhas');
    setSp(n, { replace: true });
  };

  const carregar = useCallback(async () => {
    try { setD(await verEstufa()); } catch (e) { setErroCarga(e); }
  }, []);
  useEffect(() => { void carregar(); }, [carregar]);

  const naEstufa = d?.naEstufa ?? [];
  const escolhida = naEstufa.find((l: any) => l.linha_id === linhaId) ?? null;

  /* A balança lembrada no aparelho volta escolhida quando a lista de
     equipamentos chega — e só se ainda é uma balança apta: a que venceu ou
     foi bloqueada desde ontem não volta, para não ser recusada na gravação. */
  useEffect(() => {
    if (balancaId || !lembrada) return;
    const apta = equip.some((e) => e.id === lembrada && e.categoria === 'balanca'
      && !e.impedimento && e.apto !== false);
    if (apta) setBalancaId(lembrada);
  }, [balancaId, lembrada, equip]);

  /* Sem balança lembrada, ela começa na da pesagem úmida da cápsula
     escolhida — o caso comum é a mesma balança. */
  useEffect(() => {
    if (!balancaId && escolhida?.balanca_umida_id) setBalancaId(escolhida.balanca_umida_id);
  }, [balancaId, escolhida]);

  /* A cada troca, o aparelho guarda a balança da vez. */
  useEffect(() => { if (balancaId) lembrarBalanca(balancaId); }, [balancaId]);

  const opcoes = useMemo(() => naEstufa.map((l: any) => ({
    valor: l.linha_id,
    codigo: l.capsula,
    detalhe: `${l.sigla} ${nomeDaAmostra(daApi(l, true))} · ${l.rotulo_linha}`,
  })), [naEstufa]);

  /* As que passaram do limiar, contadas com o relógio da tela: a cápsula
     que cruza as 24 h com a Estufa aberta entra sem recarregar. */
  const emAlerta = naEstufa.filter((l: any) => passouDoAlerta(l.umido_em, agora, horas)).length;

  const minhas = naEstufa.filter((l: any) => eu && l.umido_por === eu.usuarioId).length;

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const base = naEstufa
      .filter((l: any) => !soAlerta || passouDoAlerta(l.umido_em, agora, horas))
      .filter((l: any) => !soMinhas || !eu || l.umido_por === eu.usuarioId);
    if (!t) return base;
    return base.filter((l: any) => [l.capsula, l.sigla, l.amostra_codigo, l.amostra_identificacao, l.os_numero, l.cliente]
      .some((v) => String(v ?? '').toLowerCase().includes(t)));
  }, [naEstufa, busca, soAlerta, soMinhas, eu, agora, horas]);
  const pag = usePaginacao(filtradas, 50);

  const falta = !linhaId ? 'Escolha a cápsula.'
    : !balancaId ? 'Escolha a balança.'
    : !massa.trim() ? 'Digite a massa da cápsula + solo seco.' : null;

  /* O cursor só muda de lugar se ninguém o levou a outro campo enquanto a
     tela esperava (a gravação, a recarga, os 60 ms): ele está no corpo da
     página, no formulário «Pesar», ou ainda no botão que foi tocado. Quem
     foi para a busca durante a recarga não perde o que está digitando. */
  const cursorLivre = (tocado?: Element | null) => {
    const ativo = document.activeElement;
    return !ativo || ativo === document.body || ativo === tocado
      || !!secaoPesar.current?.contains(ativo);
  };

  const gravar = async () => {
    if (falta || gravando) return;
    const m = Number(massa.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(m) || m <= 0) { setErro(new Error('Massa inválida.')); return; }
    setGravando(true); setErro(null);
    try {
      const r = await pesarSeca({ linhaId, massaCapsulaSoloSeco: m, balancaEquipamentoId: balancaId });
      setUltima(r); setMassa(''); setLinhaId('');
      /* Uma cápsula a menos na estufa: o número da barra (308) relê
         (Ruling R11). Pelo evento da janela: o aviso da janela não amarra
         quem grava a quem conta. */
      window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
      await carregar();
      /* A próxima da série: o cursor volta à cápsula, e não à balança, que
         é o primeiro campo da grade e já está escolhida. */
      window.setTimeout(() => {
        if (cursorLivre()) caixaCapsula.current?.querySelector<HTMLInputElement>('input')?.focus();
      }, 60);
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  /* O «Pesar» do cartão (celular): escolhe a cápsula, sobe até o formulário
     e põe o cursor na massa seca — a cápsula na mão já está escolhida. O
     botão tocado fica com o foco até lá (o navegador o dá a ele). */
  const pesarDaLista = (linha: string) => {
    const tocado = document.activeElement;
    setLinhaId(linha);
    secaoPesar.current?.scrollIntoView({ block: 'start' });
    window.setTimeout(() => {
      if (cursorLivre(tocado)) {
        secaoPesar.current?.querySelector<HTMLInputElement>('.dd-massa-seca input')?.focus();
      }
    }, 60);
  };

  if (erroCarga && !d) return <ErroDaApi erro={erroCarga} />;
  if (!d) return <Carregando texto="Abrindo a estufa" />;

  const r = d.resumo;
  const podeTravado = pode('ensaio.corrigir_lancamento');

  const linhasEstufa: LinhaDeLancamento[] = pag.visiveis.map((l: any) => {
    const alerta = passouDoAlerta(l.umido_em, agora, horas);
    const classes = [l.linha_id === linhaId ? 'dd-fresca' : '', alerta ? 'ne-linha-alerta' : '']
      .filter(Boolean).join(' ');
    return {
      chave: l.linha_id,
      classe: classes || undefined,
      titulo: <><span className="cod">{l.capsula}</span> · {l.sigla} <NomeDaAmostra amostra={daApi(l, true)} emLinha /></>,
      resultado: <span className="n">{tempoDeEstufa(l.umido_em, agora)}
        <SeloDaEstufa desde={l.umido_em} agora={agora} horas={horas} /></span>,
      celulas: [
        /* No cartão do celular, o código já está no título: a célula é o
           «Pesar». Na tabela, o código continua sendo o botão. */
        estreito
          ? <button type="button" className="botao secundario pesar-capsula"
              onClick={() => pesarDaLista(l.linha_id)}
              aria-label={`Pesar a cápsula ${l.capsula}`}>Pesar</button>
          : <button type="button" className="botao discreto pequeno cod"
              onClick={() => setLinhaId(l.linha_id)}
              aria-label={`Pesar a cápsula ${l.capsula}`}>{l.capsula}</button>,
        <Link to={fichaDe(l)}>{l.sigla}</Link>,
        /* RN-248: a estufa mistura OS — o nome do cliente, o código e a OS. */
        <NomeDaAmostra amostra={daApi(l, true)} />,
        l.rotulo_linha,
        <>{quando(l.umido_em)}<span className="dd-quem">{l.umido_por_nome ?? ''}</span></>,
        <>{tempoDeEstufa(l.umido_em, agora)}
          <SeloDaEstufa desde={l.umido_em} agora={agora} horas={horas} /></>,
        numero(l.massa_capsula_solo_umido, 2),
      ],
    };
  });

  const linhasRecentes: LinhaDeLancamento[] = d.recentes.map((l: any) => {
    const linha = { ...l, id: l.linha_id };
    const c = correcaoPossivel(linha, 'seca', eu?.usuarioId, podeTravado, agora);
    return {
      chave: l.linha_id,
      titulo: <><span className="cod">{l.capsula}</span> · {l.sigla} <NomeDaAmostra amostra={daApi(l, true)} emLinha /></>,
      resultado: <span className="n">{numero(l.umidade, 2)} %</span>,
      semCartao: [0],
      celulas: [
        <span className="cod">{l.capsula}</span>,
        <Link to={fichaDe(l)}>{l.sigla} · {l.rotulo_linha}</Link>,
        <NomeDaAmostra amostra={daApi(l, true)} />,
        numero(l.massa_capsula_solo_seco, 2),
        <b>{l.umidade != null ? numero(l.umidade, 2) : '—'}</b>,
        <>{quando(l.seco_em)}<span className="dd-quem">{l.seco_por_nome ?? ''}</span></>,
        c ? <button type="button" className="botao secundario pequeno"
              onClick={() => setCorrigindo({ linha, c, tipo: l.tipo })}>
              {c.modo === 'janela' ? `Corrigir · até ${hora(c.ate!)}` : 'Corrigir'}
            </button>
          : <span className="dd-trava">travada</span>,
      ],
    };
  });

  return (
    <div className="tela estufa ficha-carbon dd-estufa">
      <header className="fe-cabecalho">
        <div className="fe-titulo">
          <div>
            <h1>Estufa</h1>
            <p className="sub">
              As cápsulas que aguardam a pesagem seca, de todos os ensaios que medem
              umidade — dos limites e da umidade à compactação, ao adensamento, ao
              cisalhamento, ao triaxial, à granulometria e à massa específica. Digite o
              número da cápsula e a massa; o sistema acha o ensaio.
            </p>
          </div>
        </div>
      </header>

      <dl className="cartao fe-faixa" aria-label="A estufa agora">
        <div className="fe-ind chave"><dt>Na estufa</dt><dd>{r?.na_estufa ?? 0}<span className="u">cápsulas</span></dd></div>
        <div className="fe-ind"><dt>Entraram antes de hoje</dt><dd>{r?.entraram_antes ?? 0}</dd></div>
        <div className="fe-ind"><dt>Entraram hoje</dt><dd>{r?.entraram_hoje ?? 0}</dd></div>
        <div className="fe-ind"><dt>Pesadas hoje</dt><dd>{r?.pesadas_hoje ?? 0}</dd></div>
        <div className="fe-ind"><dt>Ensaios esperando</dt><dd>{r?.ensaios ?? 0}</dd></div>
        <div className={`fe-ind${emAlerta ? ' ne-ind-alerta' : ''}`}>
          <dt>Há mais de {horasDoAlerta(horas)}</dt>
          <dd>{emAlerta}{emAlerta > 0 && <span className="u">em alerta</span>}</dd>
        </div>
      </dl>

      <section className="cartao fe-bloco dd-estufa-pesar" ref={secaoPesar}>
        <header className="fe-bloco-topo"><h2><span>Pesar</span></h2></header>
        {/* A grade também pula com Enter: a cápsula (Enter escolhe) → a massa
            → Enter grava (RN-275). A balança fica de fora (`data-teclado-fora`). */}
        <div className="fe-miolo dd-grade-pesar" {...tecladoDaTabela('Pesar')}>
          <EscolhaDeBalanca rotulo="Balança" equipamentos={equip}
            valor={balancaId} aoEscolher={setBalancaId} />
          {/* No celular a cápsula não pega o cursor ao abrir: a lista dela
              abriria sozinha e desceria por cima da faixa e do alerta das
              24 h (Ruling R27). Lá o fluxo começa pelo «Pesar» do cartão;
              depois de gravar, o cursor volta aqui (a série). */}
          <div className="dd-capsula" ref={caixaCapsula}>
            <EscolhaPorCodigo rotulo="Cápsula" opcoes={opcoes} valor={linhaId}
              aoEscolher={setLinhaId} autoFocus={!estreito}
              dica={naEstufa.length ? undefined : 'Nenhuma cápsula na estufa.'} />
            {escolhida && (
              <p className="dd-achou">
                <b>{NOME_DO_TIPO[escolhida.tipo] ?? escolhida.sigla}</b>
                {' '}· amostra <NomeDaAmostra amostra={daApi(escolhida, true)} emLinha /> · {escolhida.rotulo_linha}
                {escolhida.golpes != null ? ` (${escolhida.golpes} golpes)` : ''}
                <br />úmido {numero(escolhida.massa_capsula_solo_umido, 2)} g · tara{' '}
                {escolhida.tara != null ? `${numero(escolhida.tara, 2)} g` : '—'} · na estufa há{' '}
                {tempoDeEstufa(escolhida.umido_em, agora)}
              </p>
            )}
          </div>
          <CampoNumerico rotulo="Cápsula + solo seco (g)" className="dd-massa-seca"
            value={massa}
            onChange={(e) => setMassa(e.target.value)} />
          <div className="dd-botao-pesar">
            <button type="button" className="botao grande" data-gravar-linha=""
              aria-disabled={falta !== null || gravando} title={falta ?? 'Grava (Enter)'}
              aria-describedby={falta ? 'dd-falta-pesar' : undefined}
              onClick={() => void gravar()}>
              {gravando ? 'Gravando…' : 'Gravar'}
            </button>
            {/* O que falta, escrito ao lado — e não só no `title`, que o
                celular não mostra (P-UI-25; revisão da 349). */}
            {falta && !gravando && <p className="sub dd-falta" id="dd-falta-pesar">{falta}</p>}
          </div>
        </div>
        <RespostaDaAcao erro={erro} ok={null} />
        {ultima && (
          <Aviso tipo={ultima.contexto.alertas.length ? 'atencao' : 'ok'}
            titulo={`${ultima.linha.capsula} pesada: ${numero(ultima.linha.massa_capsula_solo_seco, 2)} g → w ${numero(ultima.linha.umidade, 2)} % · ${ultima.linha.sigla} ${nomeDaAmostra(daApi(ultima.linha, true))}, ${ultima.linha.rotulo_linha}`}>
            {ultima.contexto.alertas.length
              ? <ul className="dd-alertas">{ultima.contexto.alertas.map((a) => <li key={a}>{a}</li>)}</ul>
              : ultima.contexto.aguardando
                ? `Esse ensaio ainda tem ${ultima.contexto.aguardando} cápsula(s) na estufa.`
                : 'Era a última cápsula desse ensaio na estufa.'}
          </Aviso>
        )}
      </section>

      <section className="cartao fe-bloco">
        <header className="fe-bloco-topo">
          <h2><span>Na estufa</span></h2>
          <Selo tom={naEstufa.length ? 'andamento' : 'ok'}>{naEstufa.length}</Selo>
        </header>
        {/* No celular o alternador mora na folha dos filtros (309): quem chega
            pelo Painel (`?alerta=1`) vê o «Filtros» com o número, e o
            «Limpar» da folha o desliga (Ruling R49). */}
        <BarraDeLista rotulo="Buscar na estufa" ativos={(soAlerta ? 1 : 0) + (soMinhas ? 1 : 0)}
          aoLimpar={limparFiltros} total={filtradas.length}>
          <BuscaDaBarra valor={busca} aoMudar={setBusca}
            placeholder="Buscar cápsula, amostra, OS ou cliente" rotulo="Buscar na estufa" />
          <AlternarDaBarra ligado={soAlerta} aoMudar={mudarSoAlerta}>
            Só as de mais de {horasDoAlerta(horas)}{emAlerta ? ` (${emAlerta})` : ''}
          </AlternarDaBarra>
          <AlternarDaBarra ligado={soMinhas} aoMudar={mudarSoMinhas}>
            Minhas cápsulas{minhas ? ` (${minhas})` : ''}
          </AlternarDaBarra>
        </BarraDeLista>
        {filtradas.length === 0
          ? <p className="sub dd-vazio">{!naEstufa.length ? 'A estufa está vazia.'
              : soMinhas && !soAlerta && !busca.trim()
                ? 'Nenhuma cápsula sua na estufa.'
                : soAlerta && !busca.trim()
                  ? `Nenhuma cápsula${soMinhas ? ' sua' : ''} na estufa há mais de ${horasDoAlerta(horas)}.`
                  : 'Nada com essa busca.'}</p>
          : <TabelaDeLancamento rotulo="Cápsulas na estufa" classe="fe-lanc dd-lista-estufa"
              colunas={[
                /* Sem rótulo no cartão: o «Pesar» ocupa a largura dele. */
                { texto: estreito ? '' : 'Cápsula' }, { texto: 'Ensaio' }, { texto: 'Amostra · OS' },
                { texto: 'Linha' }, { texto: 'Entrou na estufa' }, { texto: 'Há', num: true },
                { texto: 'Cáps. + solo úmido (g)', num: true },
              ]}
              linhas={linhasEstufa} />}
        <RodapeDaLista pagina={pag.pagina} paginas={pag.paginas} setPagina={pag.setPagina}
          total={pag.total} porPagina={pag.porPagina} ordem="a mais antiga na estufa primeiro" />
      </section>

      <section className="cartao fe-bloco">
        <header className="fe-bloco-topo"><h2><span>Pesadas nas últimas 12 horas</span></h2></header>
        {d.recentes.length === 0
          ? <p className="sub dd-vazio">Nenhuma pesagem seca nas últimas 12 horas.</p>
          : <TabelaDeLancamento rotulo="Pesadas nas últimas 12 horas" classe="fe-lanc"
              colunas={[
                { texto: 'Cápsula' }, { texto: 'Ensaio' }, { texto: 'Amostra' },
                { texto: 'Cáps. + solo seco (g)', num: true }, { texto: 'w (%)', num: true },
                { texto: 'Pesada' }, { texto: '' },
              ]}
              linhas={linhasRecentes} />}
      </section>

      <p className="sub dd-rodape-estufa">
        Cápsula que está aqui não aceita linha nova em nenhum ensaio até ser pesada seca
        — ou até a linha dela ser descartada na ficha do ensaio (RN-237). Quem pesou
        corrige por {d.janelaMin} min; depois, só com a permissão de corrigir e motivo (RN-236).
        A cápsula fica aqui sem prazo; passadas {horasDoAlerta(horas)} ela entra em alerta,
        mas continua aceitando a pesagem seca (RN-250).
      </p>

      <JanelaDeCorrecao alvo={corrigindo ? { linha: corrigindo.linha, c: corrigindo.c } : null}
        tipo={corrigindo?.tipo ?? 'umidade'} opcoesCapsula={[]}
        aoFechar={() => setCorrigindo(null)} aoGravado={() => void carregar()} />
    </div>
  );
}
